import { useEffect, useState } from "react";
import { loadGreenLayout, loadPinSheet, savePinSheetHole, clearPinSheetHole, clearAllPinSheetHoles, type PinSheetEntry } from "@/lib/data/firestoreDAL";
import { getHoleTargetPreset, imageNormToLatLng, frontOffsetToLatLng, interpolateWidthProfile, type WidthProfilePoint } from "@/lib/holeTargetPresets";
import { gpsOffsetToImageNorm, imageFracToXNorm, xNormToImageFrac, getTapImageMarginRatios } from "@/lib/greenVectors";

type Props = {
  courseId: string;
  onClose: () => void;
};

type EntryMode = "tap" | "distance";

export default function PinSheetSetup({ courseId, onClose }: Props) {
  const [hole, setHole] = useState(1);
  const [pins, setPins] = useState<Record<number, PinSheetEntry>>({});
  const [entryMode, setEntryMode] = useState<EntryMode>("distance");

  const [greenLayoutUrl, setGreenLayoutUrl] = useState<string | null>(null);
  const [greenLayoutLoading, setGreenLayoutLoading] = useState(false);
  const [greenLayoutError, setGreenLayoutError] = useState<string | null>(null);
  // vectorTapImageUrl/vectorFrameCalibration: same fields, same purpose as
  // ShotPlanner.tsx's copies — a tap here lands on the same pixel-exact
  // crop the green-read vectors are normalized against, and converting it
  // to lat/lng needs the same per-hole orientation/size calibration
  // (imageUrl, the full-page photo, is a DIFFERENT crop/frame and was
  // silently feeding wrong lat/lng into both the putt read and the
  // approach-shot target on any hole where the real calibration differs
  // from holeAzimuthDeg + the 45ft default — confirmed to happen, e.g.
  // Brentwood hole 16).
  const [vectorTapImageUrl, setVectorTapImageUrl] = useState<string | null>(null);
  const [vectorFrameCalibration, setVectorFrameCalibration] = useState<{
    imageUpTrueBearingDeg: number;
    greenHalfWidthFt: number;
    greenHalfDepthFt: number;
  } | null>(null);
  // Per-axis ratio of vectorTapImageUrl's padded half-dimension to the
  // green's true half-dimension (see getTapImageMarginRatios).
  const [tapImageMarginRatios, setTapImageMarginRatios] = useState<{ x: number; y: number }>({
    x: 1,
    y: 1,
  });
  // Per-hole shape profile for distance-entry mode (see WidthProfilePoint).
  const [widthProfile, setWidthProfile] = useState<WidthProfilePoint[] | null>(null);

  const [saveStatus, setSaveStatus] = useState<"idle" | "saving" | "saved" | "error">("idle");
  const [clearAllArmed, setClearAllArmed] = useState(false);
  const [clearingAll, setClearingAll] = useState(false);

  // Pin sheets (and EZLocator) always report these in YARDS, never feet —
  // confirmed against real EZLocator screenshots where entering the sheet's
  // yardage numbers straight into fields previously labeled "(ft)" (with no
  // unit conversion before use) put the pin ~3x too close to center on
  // every hole tested (1, 3, 4, 5), a totally separate bug from the
  // imageUpTrueBearingDeg rotation work.
  const [frontYds, setFrontYds] = useState<string>("");
  const [lateralYds, setLateralYds] = useState<string>("");
  const [lateralSide, setLateralSide] = useState<"left" | "right">("right");

  const preset = getHoleTargetPreset(courseId, hole);
  const currentPin = pins[hole];
  const missingPreset = !preset?.greenCenter || preset.holeAzimuthDeg == null;

  // Load today's pin sheet once on mount (so re-opening resumes progress).
  useEffect(() => {
    let cancelled = false;
    loadPinSheet(courseId).then((result) => {
      if (!cancelled) setPins(result);
    });
    return () => { cancelled = true; };
  }, [courseId]);

  // Load the green image for whichever hole is showing.
  useEffect(() => {
    let cancelled = false;
    setGreenLayoutLoading(true);
    setGreenLayoutError(null);
    setGreenLayoutUrl(null);
    setVectorTapImageUrl(null);
    setVectorFrameCalibration(null);
    setTapImageMarginRatios({ x: 1, y: 1 });
    setWidthProfile(null);

    loadGreenLayout(courseId, hole)
      .then((layout) => {
        if (cancelled) return;
        setGreenLayoutUrl(layout?.imageUrl ?? null);
        setVectorTapImageUrl(layout?.vectorTapImageUrl ?? null);
        setVectorFrameCalibration(
          layout?.imageUpTrueBearingDeg != null &&
            layout?.greenHalfWidthFt != null &&
            layout?.greenHalfDepthFt != null
            ? {
                imageUpTrueBearingDeg: layout.imageUpTrueBearingDeg,
                greenHalfWidthFt: layout.greenHalfWidthFt,
                greenHalfDepthFt: layout.greenHalfDepthFt,
              }
            : null
        );
        setTapImageMarginRatios(layout ? getTapImageMarginRatios(layout) : { x: 1, y: 1 });
        setWidthProfile(layout?.widthProfile && layout.widthProfile.length >= 2 ? layout.widthProfile : null);
      })
      .catch((err: any) => {
        if (cancelled) return;
        setGreenLayoutError(err?.message ?? "Failed to load green image.");
      })
      .finally(() => {
        if (!cancelled) setGreenLayoutLoading(false);
      });

    return () => { cancelled = true; };
  }, [courseId, hole]);

  // Reset the per-hole distance inputs and save status when moving to a new hole.
  useEffect(() => {
    setFrontYds("");
    setLateralYds("");
    setSaveStatus("idle");
    setClearAllArmed(false);
  }, [hole]);

  const persistPin = async (entry: PinSheetEntry) => {
    const previous = pins[hole];
    setPins((prev) => ({ ...prev, [hole]: entry }));
    setSaveStatus("saving");

    const ok = await savePinSheetHole(courseId, hole, entry);
    if (ok) {
      setSaveStatus("saved");
    } else {
      // Don't leave a pin showing as set if it never actually saved —
      // this is what made pins look like they "moved around": the tap
      // was accepted locally but silently failed to persist, so the
      // next reload showed something different (or nothing).
      setPins((prev) => {
        const next = { ...prev };
        if (previous) next[hole] = previous;
        else delete next[hole];
        return next;
      });
      setSaveStatus("error");
    }
  };

  const handleTap = (xNorm: number, yNorm: number) => {
    if (!preset?.greenCenter || preset.holeAzimuthDeg == null) return;
    // The tap lands on vectorTapImageUrl (or greenLayoutUrl as a fallback
    // when a hole has no vector-aligned crop yet) — its "up" direction is
    // imageUpTrueBearingDeg, not holeAzimuthDeg, whenever real calibration
    // exists for this hole (see the field comments in firestoreDAL.ts).
    const { lat, lng } = imageNormToLatLng({
      xNorm,
      yNorm,
      greenCenter: preset.greenCenter,
      holeAzimuthDeg: vectorFrameCalibration?.imageUpTrueBearingDeg ?? preset.holeAzimuthDeg,
      greenHalfWidthFt: vectorFrameCalibration?.greenHalfWidthFt,
      greenHalfDepthFt: vectorFrameCalibration?.greenHalfDepthFt,
    });
    persistPin({ xNorm, yNorm, lat, lng });
  };

  const handleSaveDistance = () => {
    if (!preset?.greenCenter || preset.holeAzimuthDeg == null) return;
    const frontYdsNum = Number(frontYds);
    const lateralYdsNum = Number(lateralYds) || 0;
    if (!Number.isFinite(frontYdsNum) || frontYdsNum < 0) return;
    const front = frontYdsNum * 3;
    const lateral = lateralYdsNum * 3;

    // "Front" and "left/right" are real-world pin-sheet concepts tied to
    // the actual approach corridor, so this conversion correctly uses the
    // real holeAzimuthDeg regardless of calibration — only the green's
    // real measured depth (replacing the generic 45ft default) matters here.
    const { lat, lng } = frontOffsetToLatLng({
      distanceFromFrontFt: front,
      lateralFt: Math.abs(lateral),
      lateralSide,
      greenCenter: preset.greenCenter,
      holeAzimuthDeg: preset.holeAzimuthDeg,
      greenHalfDepthFt: vectorFrameCalibration?.greenHalfDepthFt,
      greenHalfWidthFt: vectorFrameCalibration?.greenHalfWidthFt,
      widthProfile: widthProfile ?? undefined,
    });

    // Derive xNorm/yNorm to draw the marker on the DISPLAYED image below,
    // which needs the image's own calibrated orientation/size (not the
    // real-world approach frame used above) — same two-step conversion
    // ShotPlanner.tsx uses for its GPS-ball/pin-sheet fallback.
    const halfDepthFt = vectorFrameCalibration?.greenHalfDepthFt ?? 45;
    const halfWidthFt = vectorFrameCalibration?.greenHalfWidthFt ?? 45;
    const ftForwardReal = -halfDepthFt + front;
    // lateral is distance from the NEAREST edge (matches EZLocator/real pin
    // sheets), not from center -- see frontOffsetToLatLng's docstring.
    // Clamped at 0 so an underestimated halfWidthFt can't flip the pin to
    // the wrong side of center -- see frontOffsetToLatLng's comment.
    // When a widthProfile is available, it replaces the flat halfWidthFt
    // AND supplies a centerline offset -- a single half-width assumes a
    // rectangle, which measurably breaks down on tapered/curving greens
    // (see WidthProfilePoint's docstring). Uses the SAME profile and the
    // SAME front-to-back fraction as frontOffsetToLatLng above so the
    // marker drawn here lines up with the lat/lng just computed.
    let ftRightReal: number;
    if (widthProfile && widthProfile.length >= 2) {
      const frac = Math.max(0, Math.min(1, front / (2 * halfDepthFt)));
      const { halfWidthFt: localHalfWidthFt, centerOffsetFt } = interpolateWidthProfile(widthProfile, frac);
      ftRightReal = centerOffsetFt + (lateralSide === "right" ? 1 : -1) * Math.max(0, localHalfWidthFt - Math.abs(lateral));
    } else {
      ftRightReal = (lateralSide === "right" ? 1 : -1) * Math.max(0, halfWidthFt - Math.abs(lateral));
    }
    const azRad = (preset.holeAzimuthDeg * Math.PI) / 180;
    const ftNorth = ftForwardReal * Math.cos(azRad) - ftRightReal * Math.sin(azRad);
    const ftEast = ftForwardReal * Math.sin(azRad) + ftRightReal * Math.cos(azRad);
    const { xNorm, yNorm } = gpsOffsetToImageNorm({
      ftNorth,
      ftEast,
      holeAzimuthDeg: vectorFrameCalibration?.imageUpTrueBearingDeg ?? preset.holeAzimuthDeg,
      greenHalfWidthFt: vectorFrameCalibration?.greenHalfWidthFt,
      greenHalfDepthFt: vectorFrameCalibration?.greenHalfDepthFt,
    });

    persistPin({
      xNorm: Math.max(0, Math.min(1, xNorm)),
      yNorm: Math.max(0, Math.min(1, yNorm)),
      lat,
      lng,
    });
  };

  const handleClear = async () => {
    const previous = pins[hole];
    setPins((prev) => {
      const next = { ...prev };
      delete next[hole];
      return next;
    });
    const ok = await clearPinSheetHole(courseId, hole);
    if (!ok && previous) {
      setPins((prev) => ({ ...prev, [hole]: previous }));
    }
  };

  const handleClearAll = async () => {
    if (!clearAllArmed) {
      setClearAllArmed(true);
      return;
    }
    const previous = pins;
    const holesToClear = Object.keys(previous).map(Number);
    setClearAllArmed(false);
    if (!holesToClear.length) return;

    setClearingAll(true);
    setPins({});
    const ok = await clearAllPinSheetHoles(courseId, holesToClear);
    if (!ok) {
      setPins(previous);
    }
    setClearingAll(false);
  };

  const setCount = Object.keys(pins).length;

  return (
    <div className="fixed inset-0 z-50 bg-slate-950 overflow-y-auto">
      <div className="max-w-2xl mx-auto p-4 space-y-4 pb-10">
        <div className="flex items-center justify-between">
          <h1 className="text-base font-bold text-white">Set today's pins</h1>
          <div className="flex items-center gap-2">
            {setCount > 0 && (
              <button
                type="button"
                onClick={handleClearAll}
                onBlur={() => setClearAllArmed(false)}
                disabled={clearingAll}
                className={`px-3 py-1 text-xs rounded border disabled:opacity-50 ${
                  clearAllArmed
                    ? "border-red-500 bg-red-900/40 text-red-300"
                    : "border-slate-500 bg-slate-900 text-slate-300 hover:bg-slate-800"
                }`}
              >
                {clearingAll ? "Clearing…" : clearAllArmed ? "Tap again to confirm" : "Clear all pins"}
              </button>
            )}
            <button
              type="button"
              onClick={onClose}
              className="px-3 py-1 text-xs rounded border border-slate-500 bg-slate-900 text-slate-200 hover:bg-slate-800"
            >Done</button>
          </div>
        </div>

        <div className="flex items-center justify-between bg-slate-800/60 border border-slate-700 rounded-xl p-2">
          <button
            type="button"
            onClick={() => setHole((h) => Math.max(1, h - 1))}
            disabled={hole <= 1}
            className="px-3 py-2 text-sm rounded bg-slate-900 text-slate-200 border border-slate-600 disabled:opacity-40"
          >← Prev</button>
          <div className="text-sm font-semibold text-slate-200 text-center">
            <div>
              Hole {hole} of 18
              {currentPin && <span className="ml-2 text-emerald-400">✓ set</span>}
            </div>
            <div className="text-xs font-normal text-slate-500">{setCount}/18 set</div>
          </div>
          <button
            type="button"
            onClick={() => setHole((h) => Math.min(18, h + 1))}
            disabled={hole >= 18}
            className="px-3 py-2 text-sm rounded bg-slate-900 text-slate-200 border border-slate-600 disabled:opacity-40"
          >Next →</button>
        </div>

        {/* Coverage strip — at-a-glance view of which holes still need a pin */}
        <div className="grid grid-cols-9 gap-1">
          {Array.from({ length: 18 }, (_, i) => i + 1).map((h) => {
            const set = !!pins[h];
            const isCurrent = h === hole;
            return (
              <button
                key={h}
                type="button"
                onClick={() => setHole(h)}
                title={set ? `Hole ${h}: pin set` : `Hole ${h}: no pin yet`}
                className={`py-1.5 text-xs rounded border font-semibold ${
                  isCurrent
                    ? "border-blue-400 ring-1 ring-blue-400"
                    : "border-slate-700"
                } ${set ? "bg-emerald-700/70 text-emerald-100" : "bg-slate-800 text-slate-500"}`}
              >{h}</button>
            );
          })}
        </div>

        {missingPreset && (
          <p className="text-xs text-amber-400">
            No surveyed green coordinates/azimuth for this hole yet — pin can't be
            converted to a real position.
          </p>
        )}

        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => setEntryMode("distance")}
            className={`flex-1 py-2 text-sm rounded border font-semibold ${entryMode === "distance" ? "bg-blue-600 text-white border-blue-700" : "bg-slate-800 text-slate-300 border-slate-600"}`}
          >Front/side distance</button>
          <button
            type="button"
            onClick={() => setEntryMode("tap")}
            className={`flex-1 py-2 text-sm rounded border font-semibold ${entryMode === "tap" ? "bg-blue-600 text-white border-blue-700" : "bg-slate-800 text-slate-300 border-slate-600"}`}
          >Tap on image</button>
        </div>

        {entryMode === "distance" && (
          <section className="bg-slate-800/60 border border-slate-700 rounded-xl p-4 space-y-3">
            <p className="text-xs text-slate-400">
              Enter the pin sheet numbers directly — distance in from the front edge
              of the green, and left/right distance from the nearest edge (matches
              EZLocator and most paper pin sheets).
            </p>
            <div className="flex items-center gap-2">
              <label className="text-xs text-slate-400 w-24">From front (yds)</label>
              <input
                type="number"
                inputMode="numeric"
                value={frontYds}
                onChange={(e) => setFrontYds(e.target.value)}
                disabled={missingPreset}
                className="flex-1 rounded bg-slate-900 border border-slate-600 px-2 py-2 text-sm text-white placeholder-slate-500"
                placeholder="e.g. 24"
              />
            </div>
            <div className="flex items-center gap-2">
              <label className="text-xs text-slate-400 w-24">From edge (yds)</label>
              <input
                type="number"
                inputMode="numeric"
                value={lateralYds}
                onChange={(e) => setLateralYds(e.target.value)}
                disabled={missingPreset}
                className="flex-1 rounded bg-slate-900 border border-slate-600 px-2 py-2 text-sm text-white placeholder-slate-500"
                placeholder="e.g. 6"
              />
              <div className="flex gap-1">
                {(["left", "right"] as const).map((side) => (
                  <button
                    key={side}
                    type="button"
                    onClick={() => setLateralSide(side)}
                    disabled={missingPreset}
                    className={`px-3 py-2 text-xs rounded border font-semibold ${lateralSide === side ? "bg-blue-600 text-white border-blue-700" : "bg-slate-900 text-slate-300 border-slate-600"}`}
                  >{side}</button>
                ))}
              </div>
            </div>
            <button
              type="button"
              onClick={handleSaveDistance}
              disabled={missingPreset || frontYds === ""}
              className="w-full py-2 text-sm rounded bg-emerald-700 hover:bg-emerald-600 font-semibold disabled:opacity-40"
            >Set pin from distances</button>
          </section>
        )}

        <section className="bg-slate-800/60 border border-slate-700 rounded-xl p-4">
          {entryMode === "tap" && (
            <p className="text-xs text-slate-500 mb-2">Pinch to zoom in before tapping for a more precise placement.</p>
          )}
          {greenLayoutLoading && <p className="text-xs text-slate-400">Loading…</p>}
          {!greenLayoutLoading && greenLayoutError && (
            <p className="text-xs text-red-400">{greenLayoutError}</p>
          )}
          {!vectorTapImageUrl && greenLayoutUrl && (
            <p className="text-xs text-amber-400 mb-2">
              No vector-aligned image for this hole yet — taps below use the full page photo, which won't line up precisely with slope data.
            </p>
          )}
          {!greenLayoutLoading && !greenLayoutError && (vectorTapImageUrl ?? greenLayoutUrl) && (
            <div
              className={`relative w-full mx-auto select-none ${
                entryMode === "tap" && !missingPreset ? "cursor-crosshair" : "pointer-events-none"
              }`}
              style={{ touchAction: entryMode === "tap" ? "pinch-zoom" : undefined }}
              onClick={(e) => {
                if (entryMode !== "tap") return;
                const rect = e.currentTarget.getBoundingClientRect();
                const rawX = (e.clientX - rect.left) / rect.width;
                const rawY = (e.clientY - rect.top) / rect.height;
                // vectorTapImageUrl is padded with margin beyond the green's
                // real half-dimensions (see tapImageMarginRatios) --
                // greenLayoutUrl (the full-page-photo fallback) is not.
                const x = vectorTapImageUrl ? imageFracToXNorm(rawX, tapImageMarginRatios.x) : rawX;
                const y = vectorTapImageUrl ? imageFracToXNorm(rawY, tapImageMarginRatios.y) : rawY;
                handleTap(Math.max(0, Math.min(1, x)), Math.max(0, Math.min(1, y)));
              }}
            >
              <img
                src={(vectorTapImageUrl ?? greenLayoutUrl) as string}
                alt={`Green layout hole ${hole}`}
                className="w-full rounded shadow pointer-events-none"
                draggable={false}
              />
              {currentPin && (
                <div
                  className="absolute pointer-events-none"
                  style={{
                    left: `${(vectorTapImageUrl ? xNormToImageFrac(currentPin.xNorm, tapImageMarginRatios.x) : currentPin.xNorm) * 100}%`,
                    top: `${(vectorTapImageUrl ? xNormToImageFrac(currentPin.yNorm, tapImageMarginRatios.y) : currentPin.yNorm) * 100}%`,
                    transform: "translate(-50%, -50%)",
                  }}
                >
                  <div className="w-5 h-5 rounded-full border-2 border-yellow-400 bg-yellow-400/30 flex items-center justify-center">
                    <div className="w-1.5 h-1.5 rounded-full bg-yellow-400" />
                  </div>
                </div>
              )}
            </div>
          )}
          {!greenLayoutLoading && !greenLayoutError && !vectorTapImageUrl && !greenLayoutUrl && (
            <p className="text-xs text-slate-400">No image saved for this hole.</p>
          )}
        </section>

        <div className="flex items-center justify-between">
          <button
            type="button"
            onClick={handleClear}
            disabled={!currentPin}
            className="px-3 py-2 text-xs rounded border border-slate-600 bg-slate-900 text-slate-300 disabled:opacity-40"
          >Clear this pin</button>

          {saveStatus === "saving" && <span className="text-xs text-slate-400">Saving…</span>}
          {saveStatus === "saved" && <span className="text-xs text-emerald-400">Saved</span>}
          {saveStatus === "error" && (
            <span className="text-xs text-red-400">
              Save failed — check your connection and try again. Pin was NOT set.
            </span>
          )}
        </div>
      </div>
    </div>
  );
}
